import { locale } from "./local";

/**
 * Formats a duration in seconds to hours
 * @param seconds - The duration in seconds to format
 * @param decimalPlaces - Number of decimal places to display (default: 1)
 * @returns Formatted hours string with unit
 */
export const formatSecondsToHours = (seconds: null | number | undefined,
	decimalPlaces = 1): string => {
	if (seconds === null || seconds === undefined) {
		return "--";
	}
	
	const hours = seconds / 3600;
	
	return new Intl.NumberFormat(locale, {
		maximumFractionDigits: decimalPlaces,
		minimumFractionDigits: decimalPlaces,
		style: "unit",
		unit: "hour",
		unitDisplay: "short",
	}).format(hours);
};

/**
 * Formats a duration in seconds to hours and minutes
 * @param seconds - The duration in seconds to format
 * @returns Formatted string in the format "X hr Y min"
 */
export const formatSecondsToHoursAndMinutes = (seconds: null | number | undefined): string => {
	if (seconds === null || seconds === undefined) {
		return "--";
	}
	
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	
	if (hours === 0) {
		return new Intl.NumberFormat(locale, {
			style: "unit",
			unit: "minute",
			unitDisplay: "short",
		}).format(minutes);
	}
	
	const hoursFormatted = new Intl.NumberFormat(locale, {
		style: "unit",
		unit: "hour",
		unitDisplay: "short",
	}).format(hours);
	
	if (minutes === 0) {
		return hoursFormatted;
	}
	
	const minutesFormatted = new Intl.NumberFormat(locale, {
		style: "unit",
		unit: "minute",
		unitDisplay: "short",
	}).format(minutes);
	
	return `${hoursFormatted} ${minutesFormatted}`;
};

/** Formats a duration in seconds as H:MM:SS. Unknown or invalid durations are omitted. */
export const formatDurationClock = (seconds: null | number | undefined): string | undefined => {
	if (seconds == null || !Number.isFinite(seconds) || seconds < 0) {
		return undefined;
	}
	const wholeSeconds = Math.round(seconds);
	const hours = Math.floor(wholeSeconds / 3600);
	const minutes = Math.floor((wholeSeconds % 3600) / 60);
	const remainder = wholeSeconds % 60;
	return `${hours}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
};
